import React, { useState, useEffect, useRef } from 'react';
import Tabs from './components/UI/Tabs';
import ConversationWindow from './components/Conversation/ConversationWindow';
import SimulationCallView from './components/Conversation/SimulationCallView';
import ScenarioSelector from './components/Conversation/ScenarioSelector';
import VoiceChatControls from './components/Conversation/VoiceChatControls';
import SettingsPanelEnhanced from './components/Settings/SettingsPanelEnhanced';
import { PracticeExercises } from './components/Practice/PracticeExercises';
import WorkspacePanel from './components/Workspace/WorkspacePanel';
import SimulationsPage from './components/Simulations/SimulationsPage';
import LearningPlansPage from './components/LearningPlans/LearningPlansPage';
import LearningPlanDetailPage from './components/LearningPlans/LearningPlanDetailPage';
import { useWebRTC } from './hooks/useWebRTC';
import { useConversation } from './hooks/useConversation';
import { useFeedback } from './components/Feedback/useFeedback';
import { DEFAULT_ENGLISH_TEACHER_PROMPT, FEEDBACK_PROMPT, MCQ_PROMPT, SUMMARY_PROMPT } from './utils/prompts';
import { Scenario, PersonalityOption, Message } from './types';
import { WorkspaceCard } from './components/Workspace/WorkspacePanel';
import ReactMarkdown from 'react-markdown';
import { promptService } from '../../services/promptService';
import { toolClient } from '../../services/toolClient';
import { learningPlansService } from '../../services/learningPlansService';
import type { LearningPlanRecord } from '../../services/learningPlansService';
import { navigateToSkills, clearUrlParams } from '../../utils/navigation';
import { DEFAULT_HEADERS } from '../../config';
import { conversationService, ConversationData } from '../../services/conversationService';

const scenarioExamples: Scenario[] = [
  {
    title: "Simulate giving feedback to a direct report",
    content: "Simulate a conversation between myself and one of my direct reports where I'm providing them feedback on a recent project. I'd like you to ask me clarifying questions and push back if you disagree."
  },
  {
    title: "Simulate a difficult sales call with a potential client",
    content: "Imagine you're a potential client and I'm trying to close an initial sale. Press me on common sticking points."
  },
  {
    title: "Provide an update to leadership on a recent project",
    content: "I want to deliver a presentation to leadership. Simulate being leadership asking probing questions."
  }
];

type Page = 'chat' | 'settings' | 'feedback' | 'practice' | 'simulations' | 'learning_plans' | 'learning_plan_detail';

interface TalkToTallyV2ProjectProps {
  user?: {
    displayName?: string;
    email?: string;
    attributes?: any;
  };
  urlParams?: {
    topic?: string;
    category?: string;
    skill?: string;
    scenario?: string;
    promptId?: string;
    vars?: string;
    autoStart?: string;
    [key: string]: string | undefined;
  };
}

const TalkToTallyV2Project: React.FC<TalkToTallyV2ProjectProps> = ({ user, urlParams }) => {
  const [currentPage, setCurrentPage] = useState<Page>('chat');
  const [error, setError] = useState<string | null>(null);
  const [showUserTranscript, setShowUserTranscript] = useState(true);
  const [showAITranscript, setShowAITranscript] = useState(true);
  const [aiVoice, setAIVoice] = useState('marin');
  const [personalityType, setPersonalityType] = useState<PersonalityOption>('english_teacher');
  // initialPrompt removed; prompt now fully provided by backend
  const [feedbackPrompt, setFeedbackPrompt] = useState(FEEDBACK_PROMPT);
  const [mcqPrompt, setMCQPrompt] = useState(MCQ_PROMPT);
  const [selectedScenario, setSelectedScenario] = useState(urlParams?.scenario || '');
  const [chatStarted, setChatStarted] = useState(false);
  const [showPracticeExercises, setShowPracticeExercises] = useState(false);
  const [conversationText, setConversationText] = useState('');
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [workspaceCards, setWorkspaceCards] = useState<WorkspaceCard[]>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [exercises, setExercises] = useState<any[]>([]);
  const [isGeneratingExercises, setIsGeneratingExercises] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [sessionTitle, setSessionTitle] = useState<string | null>(null);
  const [learningPlans, setLearningPlans] = useState<LearningPlanRecord[]>([]);
  const [learningPlansLoading, setLearningPlansLoading] = useState(false);
  const [learningPlansError, setLearningPlansError] = useState<string | null>(null);
  const [selectedLearningPlan, setSelectedLearningPlan] = useState<LearningPlanRecord | null>(null);

  const { messages, addMessage, clearMessages } = useConversation();
  // IMPORTANT: WebRTC data-channel callbacks can hold stale closures; keep a ref to latest messages
  const messagesRef = useRef<Message[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const { feedback, isFeedbackLoading, requestFeedback } = useFeedback(feedbackPrompt, setError);
  const autoStartConsumedRef = useRef<boolean>(false);

  const loadLearningPlans = async () => {
    setLearningPlansLoading(true);
    setLearningPlansError(null);
    try {
      const resp = await learningPlansService.list({ limit: 50 });
      setLearningPlans(resp.items || []);
    } catch (e: any) {
      const msg = e?.message || 'Failed to load learning plans';
      setLearningPlansError(msg);
      setLearningPlans([]);
    } finally {
      setLearningPlansLoading(false);
    }
  };
  
  // Extract user's first name from displayName
  const userFirstName = user?.displayName ? user.displayName.split(' ')[0] : null;
  
  // Auto-generate session metadata after 2-3 messages if not already done
  React.useEffect(() => {
    if (messages.length >= 2 && !sessionTitle && !window.sessionMetadata && chatStarted) {
      // Extract topic from conversation
      const userMessages = messages.filter(m => m.role === 'user').map(m => m.text).join(' ').toLowerCase();
      
      // Removed noisy auto-generation check log
      
      if (userMessages.length > 10) {
        // Generate a simple title based on key phrases
        let title = 'General Practice';
        let description = 'Business English practice session';
        let category = 'general';
        
        if (userMessages.includes('rambl') || userMessages.includes('concise') || userMessages.includes('brief') || userMessages.includes('short')) {
          title = 'Being More Concise';
          description = 'Practice getting to the point quickly in business communication';
          category = 'presentations';
        } else if (userMessages.includes('email')) {
          title = 'Email Writing';
          description = 'Practice writing effective business emails';
          category = 'emails';
        } else if (userMessages.includes('present')) {
          title = 'Presentation Skills';
          description = 'Practice delivering clear presentations';
          category = 'presentations';
        } else if (userMessages.includes('meeting')) {
          title = 'Meeting Communication';
          description = 'Practice effective meeting participation';
          category = 'meetings';
        } else if (userMessages.includes('negotiat')) {
          title = 'Negotiation Skills';
          description = 'Practice business negotiation techniques';
          category = 'negotiations';
        } else if (userMessages.includes('feedback')) {
          title = 'Giving Feedback';
          description = 'Practice constructive feedback delivery';
          category = 'feedback';
        }
        
        // Set the metadata
        window.sessionMetadata = { title, description, topic_category: category };
        setSessionTitle(title);
        // Quiet meta generation in console; visible in UI header
      }
    }
  }, [messages, sessionTitle, chatStarted]);
  
  // Handle URL parameters for pre-filled topics
  useEffect(() => {
    if (urlParams?.topic && !chatStarted) {
      let topic = urlParams.topic;
      
      // Parse context if provided
      if (urlParams.context) {
        try {
          const contextData = JSON.parse(urlParams.context);
          
          // Build a more detailed topic with context
          if (contextData.categoryDescription || contextData.skillExamples?.length > 0) {
            topic += '\n\nContext:';
            
            if (contextData.categoryDescription) {
              topic += `\nCategory focus: ${contextData.categoryDescription}`;
            }
            
            if (contextData.skillExamples?.length > 0) {
              topic += `\nRelevant skills include: ${contextData.skillExamples.join(', ')}`;
            }
          }
        } catch (err) {
          console.log('Could not parse context:', err);
        }
      }
      
      // Only add category/skill prefix if not already included
      if (urlParams.category && !topic.toLowerCase().includes(urlParams.category.toLowerCase())) {
        topic = `Practice ${urlParams.category}: ${topic}`;
      } else if (urlParams.skill && !topic.toLowerCase().includes(urlParams.skill.toLowerCase())) {
        topic = `Practice ${urlParams.skill}`;
      }
      
      setSelectedScenario(topic);
    }
  }, [urlParams]);
  
  // Handler for when Tally wants to add content to workspace
  const handleTallyWorkspaceRequest = async (
    contentType: string, 
    topic: string, 
    context: string,
    word?: string,
    definition?: string,
    examples?: string[],
    assessmentData?: any
  ) => {
    console.log('📚 Workspace request received:', { contentType, topic, context, word, definition, examples, assessmentData });
    
    if (!showWorkspace) {
      setShowWorkspace(true);
    }
    
    // Handle vocabulary content type
    if (contentType === 'vocabulary' && word && definition) {
      const vocabularyCard: WorkspaceCard = {
        id: `vocab-${Date.now()}`,
        type: 'vocabulary',
        data: {
          word,
          definition,
          examples: examples || [],
          debugAnchor: { tool: 'create_vocab_card', ts: Date.now() }
        }
      };
      setWorkspaceCards(prev => [...prev, vocabularyCard]);
      
      // Return success with the content
      return {
        success: true,
        message: 'Vocabulary added to workspace',
        content: JSON.stringify({ word, definition, examples })
      };
    }
    
    // Handle exercise content type via backend tool
    if (contentType === 'exercise') {
      const loadingCard: WorkspaceCard = {
        id: `tally-exercise-${Date.now()}`,
        type: 'exercise',
        isLoading: true,
        content: 'Creating practice exercises based on our conversation. This may take up to 30 seconds...',
        data: { jobStatusLine: '' }
      };
      setWorkspaceCards(prev => [...prev, loadingCard]);
      try {
        const conversationText = (messagesRef.current || []).map(msg => 
          `${msg.role === 'user' ? 'Student' : 'Tally'}: ${msg.text}`
        ).join('\n\n').trim();
        // Accept seed passed from Realtime handler (topic/target_phrase/abilities/skills)
        const seed = assessmentData || {};
        const payload: any = {};
        if (conversationText) payload.conversation_text = conversationText;
        if (seed.topic) payload.topic = seed.topic;
        if (seed.target_phrase) payload.target_phrase = seed.target_phrase;
        if (Array.isArray(seed.abilities)) payload.abilities = seed.abilities;
        if (Array.isArray(seed.skills)) payload.skills = seed.skills;
        console.log('🎯 Generating exercises via async job (create_exercise_card)');
        const resp = await toolClient.executeAsyncAndWait<any>(
          'create_exercise_card',
          '1.0.0',
          { ...payload, mcq_prompt: mcqPrompt },
          { client: 'web', appVersion: process.env.REACT_APP_VERSION || 'dev' },
          {
            intervalMs: 1500,
            timeoutMs: 10 * 60 * 1000,
            onUpdate: (job: any) => {
              const short = (job?.job_id || '').slice(0, 8);
              const line = `Job ${short}${short ? '…' : ''} • ${job?.status || ''}${job?.last_progress_event ? ` • ${job.last_progress_event}` : (job?.phase ? ` • ${job.phase}` : '')}`;
              setWorkspaceCards(prev =>
                prev.map(card => card.id === loadingCard.id ? { ...card, data: { ...(card.data || {}), jobStatusLine: line } } : card)
              );
            }
          }
        );
        if (!resp.ok) throw new Error(resp?.error?.message || 'exercise tool failed');
        const questions = resp.result?.questions || [];
        // Remove loading card
        setWorkspaceCards(prev => prev.filter(card => card.id !== loadingCard.id));
        // Store exercises for progression
        setExercises(prevExercises => [...prevExercises, ...questions]);
        // Show first exercise
        if (questions.length > 0) {
          const firstExercise = questions[0];
          const exerciseCard: WorkspaceCard = {
            id: `exercise-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: 'exercise',
            data: {
              question: firstExercise.content.question_text,
              options: firstExercise.content.options.map((opt: any) => opt.answer_text),
              correctAnswer: firstExercise.content.options.find((opt: any) => opt.is_correct)?.answer_text,
              fullExercise: firstExercise,
              exerciseIndex: exercises.length + currentExerciseIndex,
              debugAnchor: { tool: 'create_exercise_card', ts: Date.now() }
            }
          };
          setWorkspaceCards(prev => [...prev, exerciseCard]);
          setCurrentExerciseIndex(prev => prev + 1);
        }
        console.log('✅ Exercises added successfully (backend)');
        // Return the full questions payload so the model has access to question/answers
        try {
          const minimal = questions.map((q: any) => ({
            id: q.id || undefined,
            content: q.content,         // { question_text, options: [{ answer_text, is_correct, explanation }] }
            metadata: q.metadata || {}  // { level, ability, ... }
          }));
          return {
            success: true,
            message: 'Exercises added to workspace',
            content: JSON.stringify({ topic: seed.topic || 'Practice Exercises', questions: minimal })
          };
        } catch {
          return {
            success: true,
            message: 'Exercises added to workspace',
            content: JSON.stringify({ topic: seed.topic || 'Practice Exercises', exerciseCount: questions.length })
          };
        }
      } catch (error) {
        console.error('❌ Error generating exercises (backend):', error);
        setWorkspaceCards(prev => 
          prev.map(card => card.id === loadingCard.id ? { ...card, isLoading: false, content: 'Failed to generate exercises. Please try again.' } : card)
        );
        throw error;
      }
    }
    
    // Handle skill assessment content type
    if (contentType === 'skill_assessment' && assessmentData) {
      const assessmentCard: WorkspaceCard = {
        id: `assessment-${Date.now()}`,
        type: 'skill_assessment',
        data: { ...assessmentData, debugAnchor: { tool: 'generate_skill_assessment', ts: Date.now() } }
      };
      setWorkspaceCards(prev => [...prev, assessmentCard]);
      
      return {
        success: true,
        message: 'Skill assessment added to workspace',
        content: JSON.stringify(assessmentData)
      };
    }

    // Simulation scorecard card
    if (contentType === 'simulation_scorecard' && assessmentData) {
      const scoreCard: WorkspaceCard = {
        id: `sim-score-${Date.now()}`,
        type: 'simulation_scorecard',
        data: { ...assessmentData, debugAnchor: { tool: 'generate_simulation_scorecard', ts: Date.now() } }
      };
      setWorkspaceCards(prev => [...prev, scoreCard]);
      setShowWorkspace(true);
      return {
        success: true,
        message: 'Simulation scorecard added',
        content: JSON.stringify({ overall_score: assessmentData?.overall_score })
      };
    }

    // Learning Path (generate_tally_learning_path) via backend tool
    if (contentType === 'learning_path') {
      const loadingCard: WorkspaceCard = {
        id: `learning-path-${Date.now()}`,
        type: 'learning_path',
        isLoading: true,
        content: 'Generating a learning path...',
        data: { jobStatusLine: '' }
      };
      setWorkspaceCards(prev => [...prev, loadingCard]);
      try {
        // Only include non-empty, non-placeholder text to satisfy backend validation reliably
        const minimalTranscript = (messagesRef.current || [])
          .filter(m => typeof m.text === 'string' && m.text.trim() && m.text.trim() !== '...')
          .map(m => ({ role: m.role, text: m.text }));
        if (minimalTranscript.length < 1) {
          throw new Error('Not enough transcript yet to generate a learning path. Continue the intake questions for a bit longer, then try again.');
        }
        const constraints = assessmentData?.constraints || { minutes_per_day: 15, tally_sessions_per_week: 3 };
        const preferred_model = assessmentData?.preferred_model || 'claude-opus-4-5-20251101';
        const resp = await toolClient.executeAsyncAndWait<any>(
          'generate_tally_learning_path',
          '0.1.0',
          { transcript: minimalTranscript, constraints, preferred_model },
          { client: 'web', appVersion: process.env.REACT_APP_VERSION || 'dev' },
          {
            intervalMs: 1500,
            timeoutMs: 10 * 60 * 1000,
            onUpdate: (job: any) => {
              const short = (job?.job_id || '').slice(0, 8);
              const line = `Job ${short}${short ? '…' : ''} • ${job?.status || ''}${job?.last_progress_event ? ` • ${job.last_progress_event}` : (job?.phase ? ` • ${job.phase}` : '')}`;
              setWorkspaceCards(prev =>
                prev.map(card => card.id === loadingCard.id ? { ...card, data: { ...(card.data || {}), jobStatusLine: line } } : card)
              );
            }
          }
        );
        if (!resp.ok) {
          const msg =
            (typeof resp?.error?.message === 'string' && resp.error.message.trim())
              ? resp.error.message
              : (() => {
                  try { return JSON.stringify(resp?.error); } catch { return String(resp?.error); }
                })();
          console.error('generate_tally_learning_path tool error:', { error: resp?.error, message: msg, job_id: resp?.job_id });
          throw new Error(msg || 'learning path tool failed');
        }
        const plan = resp.result || {};
        setWorkspaceCards(prev => prev.map(card => card.id === loadingCard.id ? {
          ...card,
          isLoading: false,
          content: '',
          data: { ...plan, accepted: false, debugAnchor: { tool: 'generate_tally_learning_path', ts: Date.now() } }
        } : card));
        return {
          success: true,
          message: 'Learning path added to workspace',
          content: JSON.stringify(plan)
        };
      } catch (error) {
        console.error('❌ Error generating learning path (backend):', error);
        setWorkspaceCards(prev =>
          prev.map(card => card.id === loadingCard.id ? { ...card, isLoading: false, content: 'Failed to generate learning path. Please try again.' } : card)
        );
        throw error;
      }
    }
    
    // Concept Overview via async job (create_concept_card)
    const loadingCard: WorkspaceCard = {
      id: `tally-${Date.now()}`,
      type: 'feedback',
      isLoading: true,
      content: 'Generating content...',
      data: { jobStatusLine: '' }
    };
    setWorkspaceCards(prev => [...prev, loadingCard]);
    try {
      const durationSec = sessionStartTime ? Math.floor((Date.now() - sessionStartTime.getTime()) / 1000) : 0;
      const minimalTranscript = (messagesRef.current || []).map(m => ({ role: m.role, text: m.text }));
      const resp = await toolClient.executeAsyncAndWait<any>(
        'create_concept_card',
        '1.0.0',
        { transcript: minimalTranscript, topic: topic || 'general practice', duration: durationSec },
        { client: 'web', appVersion: process.env.REACT_APP_VERSION || 'dev' },
        {
          intervalMs: 1500,
          timeoutMs: 10 * 60 * 1000,
          onUpdate: (job: any) => {
            const short = (job?.job_id || '').slice(0, 8);
            const line = `Job ${short}${short ? '…' : ''} • ${job?.status || ''}${job?.last_progress_event ? ` • ${job.last_progress_event}` : (job?.phase ? ` • ${job.phase}` : '')}`;
            setWorkspaceCards(prev =>
              prev.map(card => card.id === loadingCard.id ? { ...card, data: { ...(card.data || {}), jobStatusLine: line } } : card)
            );
          }
        }
      );
      if (!resp.ok) throw new Error(resp?.error?.message || 'summary tool failed');
      const md = resp.result?.markdown || '';
      setWorkspaceCards(prev => prev.map(card => card.id === loadingCard.id ? { ...card, isLoading: false, content: md, data: { ...(card.data || {}), debugAnchor: { tool: 'create_concept_card', ts: Date.now() } } } : card));
      return { success: true, message: 'Content added to workspace', content: md };
    } catch (error) {
      console.error('❌ Error generating workspace content (backend):', error);
      setWorkspaceCards(prev => prev.map(card => card.id === loadingCard.id ? { ...card, isLoading: false, content: 'Failed to generate content. Please try again.' } : card));
      throw error;
    }
  };
  
  const { 
    connected, connecting, startConversation, pauseConversation, 
    resumeConversation, cleanup, remoteStreamRef, isSpeaking, 
    isAISpeaking, isPaused, disconnectCountdown, sendFarewell, startSimulation, endSimulationMode, setExtraSessionHeaders, getDebugInfo, dcRef, setRealtimeModel
  } = useWebRTC(
    setError, aiVoice, selectedScenario, addMessage, handleTallyWorkspaceRequest, userFirstName,
    (title: string, description: string, category: string) => {
      console.log('🎯 Session metadata callback called:', { title, description, category });
      setSessionTitle(title);
    },
    (info) => {
      try {
        setLocalDebugInfo(info || {});
      } catch {}
    }
  );

  const [debugOpen, setDebugOpen] = useState<boolean>(false);
  const [localDebugInfo, setLocalDebugInfo] = useState<{ session?: any; meta?: any; events?: any[] }>({});
  const [debugCollapsed, setDebugCollapsed] = useState<boolean>(false);
  const [debugScrollTarget, setDebugScrollTarget] = useState<{ tool?: string; ts?: number } | null>(null);
  const debugDrawerRef = useRef<HTMLDivElement>(null);

  // If Prompt Studio launched us with a specific prompt/variables, configure session headers for the next start
  useEffect(() => {
    try {
      const promptId = urlParams?.promptId;
      const rawVars = urlParams?.vars;
      const debugFlag = urlParams?.debug === 'true';
      const rtModel = urlParams?.rtModel;
      const rtVoice = urlParams?.voice;
      if (debugFlag) {
        try {
          localStorage.setItem('tally_debug', 'true');
          setDebugOpen(true);
        } catch {}
      }
      if (promptId) {
        let headers: Record<string, string> = { 'X-Tally-Prompt-Id': promptId };
        if (rawVars) {
          try {
            // Accept plain JSON string; if it's URL-encoded, the parseUrlParams already decoded it
            const obj = JSON.parse(rawVars);
            headers['X-Tally-Vars'] = JSON.stringify(obj);
          } catch {
            // If not valid JSON, ignore silently; backend will use defaults
          }
        }
        if (rtModel) {
          headers['X-Tally-Model'] = rtModel;
          try { setRealtimeModel(rtModel); } catch {}
        }
        if (rtVoice) {
          headers['X-Tally-Voice'] = rtVoice;
        }
        setExtraSessionHeaders(headers);
      }
    } catch {}
  }, [urlParams?.promptId, urlParams?.vars, urlParams?.rtModel, urlParams?.voice, setExtraSessionHeaders, setRealtimeModel]);

  // Refresh debug info occasionally in debug mode
  useEffect(() => {
    if (!debugOpen) return;
    const t = setInterval(() => {
      try {
        const snap = getDebugInfo?.();
        if (snap) setLocalDebugInfo(snap);
      } catch {}
    }, 1000);
    return () => clearInterval(t);
  }, [debugOpen, getDebugInfo]);
  
  // Auto-scroll debug drawer to target tool call entry
  useEffect(() => {
    if (!debugOpen || !debugDrawerRef.current || !debugScrollTarget) return;
    try {
      const eventsVisible = (localDebugInfo?.events || []).slice(-50);
      const tool = debugScrollTarget.tool;
      const anchorTs = debugScrollTarget.ts || 0;
      let targetIdx = -1;
      // Prefer function_output entries for the tool (closest at/before anchor time)
      for (let i = eventsVisible.length - 1; i >= 0; i--) {
        const e = eventsVisible[i];
        if (e && e.name === tool && e.type === 'function_output') {
          if (!e.ts || e.ts <= anchorTs) { targetIdx = i; break; }
        }
      }
      // Fallback to any entry with matching tool name
      if (targetIdx < 0) {
        for (let i = eventsVisible.length - 1; i >= 0; i--) {
          const e = eventsVisible[i];
          if (e && e.name === tool) { targetIdx = i; break; }
        }
      }
      if (targetIdx >= 0) {
        const el = document.getElementById(`dbg-${targetIdx}`);
        if (el && debugDrawerRef.current) {
          const container = debugDrawerRef.current;
          const top = (el as HTMLElement).offsetTop - 12;
          container.scrollTo({ top, behavior: 'smooth' });
          // Clear anchor after scrolling once so we don't fight user scroll
          setTimeout(() => setDebugScrollTarget(null), 250);
        }
      }
    } catch {}
  }, [debugOpen, debugScrollTarget, localDebugInfo]);

  // If user scrolls/gestures in the debug drawer, cancel any pending anchor scroll
  useEffect(() => {
    if (!debugOpen || !debugDrawerRef.current) return;
    const el = debugDrawerRef.current;
    const cancelAnchor = () => setDebugScrollTarget(null);
    try {
      el.addEventListener('wheel', cancelAnchor, { passive: true } as any);
      el.addEventListener('touchstart', cancelAnchor, { passive: true } as any);
      el.addEventListener('mousedown', cancelAnchor, { passive: true } as any);
      el.addEventListener('scroll', () => {
        // Any manual scroll cancels anchor repeat
        cancelAnchor();
      }, { passive: true } as any);
    } catch {}
    return () => {
      try {
        el.removeEventListener('wheel', cancelAnchor as any);
        el.removeEventListener('touchstart', cancelAnchor as any);
        el.removeEventListener('mousedown', cancelAnchor as any);
        el.removeEventListener('scroll', cancelAnchor as any);
      } catch {}
    };
  }, [debugOpen]);

  const handleStartConversation = async () => {
    if (isResumedSession && resumeSessionData) {
      console.log('🚀 Resuming conversation with data:', resumeSessionData);
      await startConversation(resumeSessionData.topic, resumeSessionData);
      setIsResumedSession(false); // Reset flag after starting
    } else {
      console.log('🚀 Starting new conversation with scenario:', selectedScenario);
      setChatStarted(true);
      setSessionStartTime(new Date());
      // Clear URL params after starting conversation
      clearUrlParams();
      await startConversation();
    }
  };

  const [isResumedSession, setIsResumedSession] = useState(false);
  const [resumeSessionData, setResumeSessionData] = useState<any>(null);

  const handleResumeConversation = async (sessionData: ConversationData) => {
    console.log('📂 Resuming conversation:', sessionData.sessionId);
    
    // Close settings page
    setCurrentPage('chat');
    
    // Set up the UI state
    setChatStarted(true);
    setSelectedScenario(sessionData.topic || '');
    setSessionTitle(sessionData.title || null);
    
    // Restore session metadata
    if (sessionData.title || sessionData.description) {
      window.sessionMetadata = {
        title: sessionData.title || '',
        description: sessionData.description || '',
        topic_category: sessionData.topicCategory || 'general'
      };
    }
    
    // Load historical messages
    clearMessages();
    sessionData.transcript.forEach((msg: Message) => {
      addMessage(msg);
    });
    
    // Restore workspace cards
    if (sessionData.workspaceItems && sessionData.workspaceItems.length > 0) {
      setWorkspaceCards(sessionData.workspaceItems);
      setShowWorkspace(true);
    }
    
    // Store resume data for when user clicks Continue
    const lastMessage = sessionData.transcript[sessionData.transcript.length - 1];
    const lastMessageTime = lastMessage?.timestamp || sessionData.createdAt;
    
    setResumeSessionData({
      transcript: sessionData.transcript,
      sessionId: sessionData.sessionId,
      lastMessageTime: lastMessageTime,
      sessionMetadata: window.sessionMetadata,
      topic: sessionData.topic
    });
    
    setIsResumedSession(true);
    setSessionStartTime(new Date()); // Reset session timer
  };
  
  // Auto-start conversation if requested (only for Prompt Studio launches)
  useEffect(() => {
    const isPromptStudioLaunch = urlParams?.promptId || urlParams?.debug === 'true' || urlParams?.vars;
    if (urlParams?.autoStart === 'true' && isPromptStudioLaunch && !chatStarted && !connecting && !autoStartConsumedRef.current) {
      autoStartConsumedRef.current = true;
      // Immediately reflect starting state to avoid showing the start screen
      setChatStarted(true);
      setSessionStartTime(new Date());
      // Clear URL params so revisits don't auto-start again
      clearUrlParams();
      // Start without delay
      startConversation();
    }
  }, [urlParams?.autoStart, urlParams?.promptId, urlParams?.debug, urlParams?.vars, chatStarted, connecting, startConversation]);
  
  const handleStartWithScenario = async (scenario: string) => {
    console.log('🎨 Starting with specific scenario:', scenario);
    setSelectedScenario(scenario);
    setChatStarted(true);
    setSessionStartTime(new Date());
    await startConversation(scenario); // Pass scenario directly
  };
  
  // Handle button clicks with scenario
  const handleScenarioStart = (scenario: string) => {
    console.log('🎯 Button clicked with scenario:', scenario);
    handleStartWithScenario(scenario);
  };
  
  // Removed useEffect - handling directly in handleStartWithScenario

  const handlePauseResume = async () => {
    if (isPaused) {
      await resumeConversation();
    } else {
      await pauseConversation();
    }
  };

  // Removed legacy session summary generation; concept overview is now the canonical workspace card

  const storeConversationData = async () => {
    if (!sessionStartTime || messages.length === 0) {
      console.log('No conversation data to store');
      return;
    }

    try {
      const sessionEndTime = new Date();
      const duration = Math.floor((sessionEndTime.getTime() - sessionStartTime.getTime()) / 1000);
      
      // Find skill assessment from workspace cards
      const assessmentCard = workspaceCards.find(card => card.type === 'skill_assessment');
      
      // Get AI-generated metadata if available
      const metadata = window.sessionMetadata;
      
      // Prepare conversation data
      const conversationData = {
        userId: user?.email || 'anonymous',
        createdAt: sessionStartTime.toISOString(),
        topic: selectedScenario || 'general practice',
        title: metadata?.title,
        description: metadata?.description,
        topicCategory: metadata?.topic_category,
        duration: duration,
        transcript: messages,
        workspaceItems: workspaceCards,
        skillAssessment: assessmentCard?.data,
        aiVoice: aiVoice,
        userLevel: localStorage.getItem('userLevel') || undefined,
        promptMode: localStorage.getItem('tallyPromptMode') || 'local',
        promptId: urlParams?.promptId || localStorage.getItem('tallyOpenAIPromptId') || undefined,
        resolvedVariables: (() => {
          try {
            return urlParams?.vars ? JSON.parse(urlParams.vars) : undefined;
          } catch {
            return undefined;
          }
        })()
      };
      
      console.log('💾 Storing conversation data with summary...');
      await conversationService.storeConversation(conversationData);
      console.log('✅ Conversation stored successfully');
    } catch (error) {
      console.error('Failed to store conversation:', error);
      // Don't throw - we don't want to disrupt the user experience
    }
  };
  
  const handleGoBackToTopicInput = () => {
    cleanup();
    setChatStarted(false);
    setSelectedScenario('');
    clearMessages();
    setSessionStartTime(null);
    setWorkspaceCards([]);  // Clear workspace cards
    setShowWorkspace(false);  // Hide workspace
    setCurrentExerciseIndex(0);  // Reset exercise index
    setExercises([]);  // Clear exercises
    window.sessionMetadata = undefined;  // Clear session metadata
    setSessionTitle(null);  // Clear session title
  };
  
  const handleEndSession = async () => {
    const launchedFromPromptStudio = !!urlParams?.promptId || urlParams?.debug === 'true' || !!urlParams?.vars;
    if (connected && sendFarewell) {
      // Send farewell message to trigger Tally's skill assessment
      sendFarewell();
      
      // Show workspace to display the assessment card
      setShowWorkspace(true);
      
      // Wait for Tally to respond with assessment before cleanup
      setTimeout(async () => {
        // Store conversation data before cleanup
        await storeConversationData();
        
        if (launchedFromPromptStudio) {
          // Close the window if launched from Prompt Studio
          try { window.close(); } catch {}
        } else {
          handleGoBackToTopicInput();
          // Close the workspace after ending
          setShowWorkspace(false);
        }
      }, 5000); // Give Tally time to generate assessment
    } else {
      // Store conversation data even if not connected
      await storeConversationData();
      
      if (launchedFromPromptStudio) {
        try { window.close(); } catch {}
      } else {
        // If not connected, just go back
        handleGoBackToTopicInput();
        // Close the workspace after ending
        setShowWorkspace(false);
      }
    }
  };

  // Show an informational capabilities card in the shared workspace
  const handleShowTallyInfoCard = () => {
    if (!showWorkspace) {
      setShowWorkspace(true);
    }
    const infoMarkdown = `Hi, I'm Tally, your personal AI coach. As we get to know each other, I'll be recommending different lessons, exercises and providing other helpful ways for you to achieve your goals. At any point, you can also proactively ask me for a variety of things, such as exercises, vocab cards, summaries and more. Here's a list of what I can do and how to ask for it:\n\n
**Here are some things I can help with:**

- 🌐 **Speak Any Language**: You can speak in any language at any time and I'll respond in that language, to help with some of the trickier concepts in English.
- 📘 **Vocab Cards**: If you tell me you don't understand a word or a phrase, I'll create a vocab card for you.
- 🧭 **Context Cards**: If you ask me for an overview of a topic, I'll create a context card — think of it like a topic overview.
- 📝 **Conversation Summaries**: At any point, you can ask for a summary of what we've worked on.
- 🧪 **Exercises**: You can ask me for an exercise to help you practice any topic we're discussing.
- 📊 **Session Analysis**: At the end of our session, if you say goodbye or indicate that you'd like to end our session, I'll create a brief report about the skills you demonstrated.`;

    const card: WorkspaceCard = {
      id: `tally-info-${Date.now()}`,
      type: 'feedback',
      content: infoMarkdown
    };
    setWorkspaceCards(prev => [...prev, card]);
  };

  const handleRequestFeedback = async () => {
    setShowWorkspace(true);
    setWorkspaceCards([{ id: 'feedback-loading', type: 'feedback', isLoading: true }]);
    await requestFeedback(messages);
  };

  // Update workspace cards when feedback is ready
  React.useEffect(() => {
    if (feedback && !isFeedbackLoading) {
      setWorkspaceCards(prev => {
        const filtered = prev.filter(card => card.id !== 'feedback-loading');
        return [...filtered, { id: 'feedback-1', type: 'feedback', content: feedback }];
      });
    }
  }, [feedback, isFeedbackLoading]);

  const handlePractice = async () => {
    if (isGeneratingExercises || exercises.length > 0) {
      // If we already have exercises, just show the next one
      if (currentExerciseIndex < exercises.length) {
        const exercise = exercises[currentExerciseIndex];
        const newExerciseCard: WorkspaceCard = {
          id: `exercise-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          type: 'exercise',
          data: {
            question: exercise.content.question_text,
            options: exercise.content.options.map((opt: any) => opt.answer_text),
            correctAnswer: exercise.content.options.find((opt: any) => opt.is_correct)?.answer_text,
            fullExercise: exercise, // Keep full data for detailed display
            exerciseIndex: currentExerciseIndex
          }
        };
        setWorkspaceCards(prev => [...prev, newExerciseCard]);
        setCurrentExerciseIndex(prev => prev + 1);
      }
      return;
    }

    // Generate exercises using the prompt service
    setIsGeneratingExercises(true);
    const loadingCard: WorkspaceCard = {
      id: 'exercises-loading',
      type: 'exercise',
      isLoading: true,
      content: 'Generating practice exercises based on your conversation...'
    };
    setWorkspaceCards(prev => [...prev, loadingCard]);

    try {
      const conversationText = messages.map(msg => 
        `${msg.role === 'user' ? 'Student' : 'Tally'}: ${msg.text}`
      ).join('\n\n');

      const response = await promptService.executePrompt(
        [
          {
            role: 'system',
            content: mcqPrompt
          },
          {
            role: 'user',
            content: conversationText
          }
        ],
        {
          temperature: 0.7,
          max_tokens: 3000,
          top_p: 1,
          presence_penalty: 0,
          frequency_penalty: 0,
          stop: [],
          user: 'practice-exercises'
        },
        'multiple-choice-exercise'
      );

      console.log('Exercise generation response:', response);

      if (response.content && typeof response.content === 'object' && 'questions' in response.content) {
        const questions = response.content.questions;
        setExercises(questions);
        
        // Remove loading card
        setWorkspaceCards(prev => prev.filter(card => card.id !== 'exercises-loading'));
        
        // Show first exercise
        if (questions.length > 0) {
          const firstExercise = questions[0];
          const exerciseCard: WorkspaceCard = {
            id: `exercise-0`,
            type: 'exercise',
            data: {
              question: firstExercise.content.question_text,
              options: firstExercise.content.options.map((opt: any) => opt.answer_text),
              correctAnswer: firstExercise.content.options.find((opt: any) => opt.is_correct)?.answer_text,
              fullExercise: firstExercise,
              exerciseIndex: 0
            }
          };
          setWorkspaceCards(prev => [...prev, exerciseCard]);
          setCurrentExerciseIndex(1);
        }
      } else {
        throw new Error('Invalid response format from exercise generation');
      }
    } catch (error) {
      console.error('Error generating exercises:', error);
      setWorkspaceCards(prev => prev.filter(card => card.id !== 'exercises-loading'));
      
      const errorCard: WorkspaceCard = {
        id: 'exercise-error',
        type: 'result',
        content: 'Failed to generate exercises. Please try again.'
      };
      setWorkspaceCards(prev => [...prev, errorCard]);
    } finally {
      setIsGeneratingExercises(false);
    }
  };

  const handleExerciseSubmit = (exerciseId: string, answer: any) => {
    // Notify Tally of the user's selection (without revealing correct answer)
    try {
      const chosenCard = workspaceCards.find(c => c.id === exerciseId && c.type === 'exercise') as any;
      if (chosenCard && dcRef?.current && (dcRef.current as any).readyState === 'open') {
        const data = chosenCard.data || {};
        const computedCorrect = (data.correctAnswer ?? (data.options?.[1])) === answer;
        let explanationText = '';
        try {
          const sel = data?.fullExercise?.content?.options?.find?.((o: any) => o?.answer_text === answer);
          if (sel?.explanation) explanationText = String(sel.explanation);
        } catch {}
        const qIndex = (data?.exerciseIndex ?? 0) + 1;
        const userMsg = [
          `Exercise response • Question ${qIndex}:`,
          `I chose: "${String(answer)}".`,
          `Result: ${computedCorrect ? 'Correct' : 'Incorrect'}.`,
          explanationText ? `My reasoning: ${explanationText}` : '',
          `Please do not reveal the correct answer unless I explicitly ask. If I'm incorrect, you may offer a hint or explain the relevant rule instead of giving the exact answer.`
        ].filter(Boolean).join(' ');
        (dcRef.current as any).send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: userMsg }]
          }
        }));
      }
    } catch (e) {
      console.warn('Unable to notify Tally of MCQ selection:', e);
    }
    // Update the exercise card to show the result
    setWorkspaceCards(prev => prev.map(card => {
      if (card.id === exerciseId && card.type === 'exercise') {
        return {
          ...card,
          data: {
            ...card.data,
            showResult: true,
            userAnswer: answer,
            correctAnswer: card.data.correctAnswer || card.data.options[1]
          }
        };
      }
      return card;
    }));
    
    // Add next exercise or summary after a delay
    setTimeout(() => {
      if (currentExerciseIndex < exercises.length) { // Show all generated exercises
        const nextCard: WorkspaceCard = {
          id: `next-button-${currentExerciseIndex}`,
          type: 'result',
          content: 'Ready for the next question?',
          data: {
            showNextButton: true
          }
        };
        setWorkspaceCards(prev => [...prev, nextCard]);
      } else {
        // Add summary card
        const correctCount = workspaceCards.filter(card => 
          card.type === 'exercise' && 
          card.data?.showResult && 
          card.data?.userAnswer === card.data?.correctAnswer
        ).length;
        
        const summaryCard: WorkspaceCard = {
          id: 'summary',
          type: 'summary',
          content: `Great job! You completed ${exercises.length} exercises and got ${correctCount} correct.`
        };
        setWorkspaceCards(prev => [...prev, summaryCard]);
      }
    }, 1500);
  };

  const renderBackButton = (targetPage: Page = 'chat') => (
    <button 
      onClick={() => setCurrentPage(targetPage)}
      className="p-1 rounded-full hover:bg-gray-200 transition-colors"
      title="Go back"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-talaera-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
    </button>
  );

  const renderChatPage = () => (
    <div
      className="h-full flex relative p-6 pt-4 overflow-hidden"
    >
      <div 
        className="h-full flex flex-col transition-all duration-300 ease-in-out overflow-hidden"
        style={{ width: showWorkspace ? '35%' : '100%' }}
      >
      {!chatStarted ? (
        <div className="bg-white rounded-lg shadow h-full overflow-hidden">
        <div className="flex flex-col items-center justify-center h-full">
          {/* Gradient Circle and Text */}
          <div className="flex flex-col items-center justify-center flex-1">
            <h2 className="text-7xl font-bold text-gray-700 mb-6" style={{ fontFamily: 'Roboto, sans-serif' }}>Talk to Tally</h2>
            <div 
              className="w-48 h-48 rounded-full mb-8 relative overflow-hidden"
              style={{
                background: `
                  radial-gradient(circle at 30% 30%, #01BCD4 0%, transparent 50%),
                  radial-gradient(circle at 70% 60%, #8433ff 0%, transparent 50%),
                  radial-gradient(circle at 40% 80%, white 0%, transparent 50%),
                  #f0f0f0
                `,
                backgroundSize: '100% 100%',
                animation: 'gentlePulse 3.2s ease-in-out infinite',
                boxShadow: '0 0 40px rgba(147, 112, 219, 0.3), 0 0 60px rgba(255, 105, 180, 0.2), 0 0 80px rgba(0, 206, 209, 0.2)'
              }}
            >
              <style>{`
                @keyframes gentlePulse {
                  0%, 100% {
                    transform: scale(1);
                    box-shadow: 0 0 40px rgba(147, 112, 219, 0.3), 0 0 60px rgba(255, 105, 180, 0.2), 0 0 80px rgba(0, 206, 209, 0.2);
                  }
                  50% {
                    transform: scale(1.05);
                    box-shadow: 0 0 50px rgba(147, 112, 219, 0.4), 0 0 70px rgba(255, 105, 180, 0.3), 0 0 90px rgba(0, 206, 209, 0.3);
                  }
                }
                @keyframes rotateGradient {
                  0% {
                    transform: rotate(0deg);
                  }
                  100% {
                    transform: rotate(360deg);
                  }
                }
              `}</style>
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: `
                    radial-gradient(circle at 40% 40%, #01BCD4 0%, transparent 40%),
                    radial-gradient(circle at 60% 70%, #8433ff 0%, transparent 40%),
                    radial-gradient(circle at 30% 60%, white 0%, transparent 40%)
                  `,
                  animation: 'rotateGradient 16s linear infinite',
                  opacity: 0.5
                }}
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 text-center px-8 mb-8">
              What would you like to work on today?
            </h1>
            
            {/* Show category if provided */}
            {urlParams?.category && (
              <div className="text-center mb-6">
                <span className="text-gray-600">Category: </span>
                <button
                  onClick={() => {
                    navigateToSkills({ category: urlParams.category });
                  }}
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  {urlParams.category}
                </button>
              </div>
            )}
            
            {/* Removed topic suggestion buttons. Place action buttons directly under header. */}
            <div className="flex gap-4 mt-4 justify-center">
              <button
                onClick={handleStartConversation}
                disabled={connecting}
                className="px-8 py-3 text-white rounded-lg transition-colors text-lg font-medium flex items-center"
                style={{ backgroundColor: '#042434' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#063247'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#042434'}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {connecting ? 'Connecting…' : 'Lets Just Talk'}
              </button>

            <button
              onClick={() => setCurrentPage('simulations')}
                disabled={connecting}
                className="px-8 py-3 rounded-lg transition-colors text-lg font-medium flex items-center"
                style={{ backgroundColor: 'white', color: '#25394D', border: '1px solid #25394D' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                Explore Scenarios
              </button>

              <button
                onClick={async () => {
                  setCurrentPage('learning_plans');
                  await loadLearningPlans();
                }}
                disabled={connecting}
                className="px-8 py-3 rounded-lg transition-colors text-lg font-medium flex items-center"
                style={{ backgroundColor: 'white', color: '#25394D', border: '1px solid #25394D' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                Learning Plans
              </button>
            </div>

            <div className="text-center mt-3">
              <button
                onClick={() => setCurrentPage('settings')}
                className="text-gray-600 hover:text-gray-800 text-base transition-colors inline-flex items-center"
              >
                <img src="/Icon/History.svg" alt="History" className="w-4 h-4 mr-1" onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.style.display = 'none';
                }} />
                History
              </button>
            </div>
          </div>
        </div>
        </div>
      ) : (
        <>
        <div className="flex flex-col h-full overflow-hidden" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <div className="flex-1 min-h-0 px-4 pt-4 overflow-hidden">
            {selectedScenario?.toLowerCase().includes('customer support') ? (
              <SimulationCallView
                title={sessionTitle || 'Customer Support Call'}
                username={'rsawyer-48'}
                isAISpeaking={isAISpeaking}
                startTimeMs={sessionStartTime ? sessionStartTime.getTime() : undefined}
                remoteStream={remoteStreamRef.current}
                connecting={connecting}
                connected={connected}
                contactName={'Ron Sawyer'}
              />
            ) : (
              <ConversationWindow messages={messages} remoteStream={remoteStreamRef.current} userName={user?.displayName} />
            )}
          </div>

          <div className="flex gap-3 p-4 pt-2 justify-center items-center flex-shrink-0">
            <VoiceChatControls
              connected={connected}
              connecting={connecting}
              isPaused={isPaused}
              isResumedSession={isResumedSession}
              onStart={handleStartConversation}
              onPauseResume={handlePauseResume}
            />
            <button 
              className="rounded-full border-2 flex items-center justify-center hover:bg-gray-100 transition-colors"
              style={{ width: '46px', height: '46px', borderColor: '#042434' }}
            >
              <img 
                src="/Keyboard.svg" 
                alt="Type message" 
                className="w-6 h-6"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.style.display = 'none';
                }}
              />
            </button>
            <button 
              onClick={() => setShowWorkspace(!showWorkspace)}
              className="rounded-full border-2 flex items-center justify-center hover:bg-gray-100 transition-colors"
              style={{ width: '46px', height: '46px', borderColor: '#042434' }}
              title={showWorkspace ? 'Hide Workspace' : 'Show Workspace'}
            >
              <svg className="w-6 h-6" fill="none" stroke="#042434" strokeWidth="2" viewBox="0 0 24 24">
                {showWorkspace ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                )}
              </svg>
            </button>
          </div>
          
          {/* Auto-disconnect countdown notification */}
          {disconnectCountdown !== null && (
            <div 
              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"
              style={{ zIndex: 100 }}
            >
              <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">
                Session will auto-close in {disconnectCountdown} seconds
              </span>
              <button
                onClick={handlePauseResume}
                className="ml-2 text-xs bg-white text-gray-800 px-2 py-1 rounded hover:bg-gray-100"
              >
                Resume
              </button>
            </div>
          )}
        </div>
        </>
      )}
      </div>
      
      {showWorkspace && (
        <WorkspacePanel
          isOpen={showWorkspace}
          onClose={() => {
            setShowWorkspace(false);
            setWorkspaceCards([]);
            setCurrentExerciseIndex(0);
          }}
          cards={workspaceCards}
          onPractice={handlePractice}
          onExerciseSubmit={handleExerciseSubmit}
          onNextExercise={handlePractice}
          onAcceptLearningPath={async (cardId) => {
            // Persist the accepted plan before updating UI state (fail loud on errors).
            const card = workspaceCards.find(c => c.id === cardId);
            const plan = card?.data;
            if (!plan) {
              window.alert('Cannot accept learning path: missing plan data.');
              return;
            }
            if (plan.accepted) return;

            // Mark as "accepting" to prevent double submits while we persist.
            setWorkspaceCards(prev => prev.map(c => {
              if (c.id !== cardId) return c;
              return { ...c, data: { ...(c.data || {}), accepting: true, acceptError: undefined } };
            }));

            try {
              const saved = await learningPlansService.accept(plan, { source: { card_id: cardId, client: 'ttv2' } });
              setWorkspaceCards(prev => prev.map(c => {
                if (c.id !== cardId) return c;
                const nextData = {
                  ...(c.data || {}),
                  accepted: true,
                  accepting: false,
                  acceptedAt: saved.accepted_at || new Date().toISOString(),
                  plan_id: saved.plan_id,
                };
                return { ...c, data: nextData };
              }));
            } catch (e: any) {
              const msg = e?.message || 'Failed to persist learning plan';
              console.error('Accept learning path failed', e);
              setWorkspaceCards(prev => prev.map(c => {
                if (c.id !== cardId) return c;
                return { ...c, data: { ...(c.data || {}), accepting: false, acceptError: msg } };
              }));
              window.alert(`Failed to accept learning path: ${msg}`);
            }
          }}
          onOpenDebug={(anchor) => {
            setDebugOpen(true);
            setDebugCollapsed(false);
            setTimeout(() => {
              setDebugScrollTarget(anchor || null);
            }, 0);
          }}
        />
      )}
    </div>
  );

  const renderFeedbackPage = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center space-x-4 mb-6">
          {renderBackButton()}
          <h2 className="text-xl font-semibold text-gray-800">Conversation Feedback</h2>
        </div>
        
        {isFeedbackLoading ? (
          <div className="flex flex-col items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
            <p className="mt-4 text-gray-600">Analyzing your English skills...</p>
          </div>
        ) : (
          <>
            <div className="prose prose-blue max-w-none mb-6">
              <ReactMarkdown
                components={{
                  h1: ({...props}) => <h1 className="text-2xl font-bold text-talaera-blue mb-4" {...props} />,
                  h2: ({...props}) => <h2 className="text-xl font-semibold text-talaera-blue mb-3" {...props} />,
                  h3: ({...props}) => <h3 className="text-lg font-semibold text-talaera-blue mb-2" {...props} />,
                  p: ({...props}) => <p className="mb-3" {...props} />,
                  ul: ({...props}) => <ul className="mb-4 list-disc pl-6" {...props} />,
                  ol: ({...props}) => <ol className="mb-4 list-decimal pl-6" {...props} />,
                  li: ({...props}) => <li className="mb-1" {...props} />,
                  strong: ({...props}) => <strong className="text-gray-900" {...props} />,
                  em: ({...props}) => <em className="text-gray-800" {...props} />,
                }}
              >
                {feedback}
              </ReactMarkdown>
            </div>
            
            <button
              onClick={() => setCurrentPage('practice')}
              className="w-full py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              Practice These Skills
            </button>
          </>
        )}
      </div>
    </div>
  );

  const renderPracticePage = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <PracticeExercises
          conversationText={messages.map(msg => `${msg.role === 'user' ? 'Student' : 'Tally'}: ${msg.text}`).join('\n\n')}
          onClose={() => setCurrentPage('feedback')}
          mcqPrompt={mcqPrompt}
          renderBackButton={() => renderBackButton('feedback')}
        />
      </div>
    </div>
  );


  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#F5F1EF' }}>
      {/* Fixed header */}
      {currentPage === 'chat' && chatStarted && (
        <div className="flex justify-between items-center px-10 py-4 pb-0" style={{ backgroundColor: '#F5F1EF' }}>
          <button 
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            style={{
              color: '#25394D',
              fontSize: '14px',
              fontFamily: 'Lato',
              fontWeight: '700',
              wordWrap: 'break-word'
            }}
          >
            <img 
              src="/Icon/History.svg" 
              alt="History" 
              className="w-4 h-4"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.style.display = 'none';
              }}
            />
            History
          </button>
          
          {sessionTitle ? (
            <div className="text-center">
              <h2 className="text-lg font-semibold" style={{ color: '#25394D' }}>
                {sessionTitle}
              </h2>
            </div>
          ) : (
            <img 
              src="/logo.svg" 
              alt="Logo" 
              className="h-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.style.display = 'none';
              }}
            />
          )}
          
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                try {
                  const text = messages.map(m => `${m.role === 'user' ? 'Student' : 'Tally'}: ${m.text}`).join('\n');
                  await navigator.clipboard.writeText(text);
                } catch (e) {
                  console.error('Failed to copy transcript', e);
                }
              }}
              className="hover:opacity-80 transition-opacity"
              title="Copy transcript to clipboard"
            >
              <svg className="w-5 h-5" fill="none" stroke="#25394D" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h8l4 4v12a1 1 0 01-1 1h-3M8 7h8m-8 0v14a1 1 0 001 1h8m-9-6h6m-6-4h6" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentPage('settings')}
              className="hover:opacity-80 transition-opacity"
            >
              <svg className="w-5 h-5" fill="none" stroke="#25394D" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {/* Info icon between Settings and End Session */}
            <button
              onClick={handleShowTallyInfoCard}
              className="hover:opacity-80 transition-opacity"
              title="About Tally"
            >
              <svg className="w-5 h-5" fill="none" stroke="#25394D" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </button>
            
            <button
              onClick={handleEndSession}
              style={{
                backgroundColor: '#F5F1EF',
                borderRadius: '100px',
                padding: '8px 16px',
                color: '#25394D',
                fontSize: '14px',
                fontFamily: 'Lato',
                fontWeight: '700',
                wordWrap: 'break-word',
                border: '1px solid #25394D',
                cursor: 'pointer'
              }}
              className="hover:opacity-80 transition-opacity"
            >
              End Session
            </button>
          </div>
        </div>
      )}
      
      {/* Error display */}
      {error && (
        <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      )}
      
      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-hidden" style={{ backgroundColor: '#F5F1EF' }}>
        {currentPage === 'chat' && (
          <div className="h-full" style={{ backgroundColor: '#F5F1EF' }}>
            {renderChatPage()}
          </div>
        )}
        {currentPage === 'simulations' && (
          <div className="h-full p-6" style={{ backgroundColor: '#F5F1EF' }}>
            <SimulationsPage 
              onBack={() => setCurrentPage('chat')} 
              onStart={async ({ instructions, voice, topic }) => {
                // Apply simulation overrides and start the session
                setAIVoice(voice || 'coral');
                setSelectedScenario(topic || '');
                setChatStarted(true);
                setSessionStartTime(new Date());
                await startSimulation({ instructions, voice, topic });
                setCurrentPage('chat');
              }}
            />
          </div>
        )}
        {currentPage === 'learning_plans' && (
          <div className="h-full p-6" style={{ backgroundColor: '#F5F1EF' }}>
            <LearningPlansPage
              plans={learningPlans}
              isLoading={learningPlansLoading}
              error={learningPlansError}
              onBack={() => setCurrentPage('chat')}
              onOpenPlan={(p) => {
                setSelectedLearningPlan(p);
                setCurrentPage('learning_plan_detail');
              }}
            />
          </div>
        )}
        {currentPage === 'learning_plan_detail' && selectedLearningPlan && (
          <div className="h-full p-6" style={{ backgroundColor: '#F5F1EF' }}>
            <LearningPlanDetailPage
              plan={selectedLearningPlan}
              onBack={() => setCurrentPage('learning_plans')}
              onDiscussWithTally={async ({ scenario, planId, lessonIndex, lessonInstance }) => {
                // Reset any existing session and start a lesson-focused Tally session.
                try { cleanup(); } catch {}
                clearMessages();
                setSessionStartTime(new Date());
                setChatStarted(true);
                setCurrentPage('chat');
                setSelectedScenario(scenario);
                clearUrlParams();

                // Preload the lesson into the shared workspace using the same styles as the lesson view.
                try {
                  const planTitle = (selectedLearningPlan?.title || selectedLearningPlan?.plan?.title || 'Learning Plan');
                  const now = Date.now();
                  const cards: WorkspaceCard[] = [{
                    id: `lp-lesson-${now}`,
                    type: 'lesson',
                    data: {
                      planTitle,
                      planId,
                      lessonIndex,
                      lessonInstance,
                      completionStatus: (lessonInstance as any)?.completion?.status || 'not_started',
                      onExerciseAnswered: ({ lessonIndex, sectionIndex, exerciseIndex, answer, fullExercise }: any) => {
                        // Notify Tally of the user's selection (without revealing the correct answer explicitly).
                        try {
                          if (!dcRef?.current || (dcRef.current as any).readyState !== 'open') return;
                          const opts = Array.isArray(fullExercise?.content?.options) ? fullExercise.content.options : [];
                          const computedCorrect = !!opts.find((o: any) => o?.is_correct && o?.answer_text === answer);
                          let explanationText = '';
                          try {
                            const sel = opts.find((o: any) => o?.answer_text === answer);
                            if (sel?.explanation) explanationText = String(sel.explanation);
                          } catch {}
                          const userMsg = [
                            `Lesson exercise response • Lesson ${lessonIndex + 1}, Section ${sectionIndex + 1}, Question ${exerciseIndex + 1}:`,
                            `I chose: "${String(answer)}".`,
                            `Result: ${computedCorrect ? 'Correct' : 'Incorrect'}.`,
                            explanationText ? `My reasoning: ${explanationText}` : '',
                            `Please do not reveal the correct answer unless I explicitly ask. If I'm incorrect, offer a hint or explain the relevant rule instead of giving the exact answer.`
                          ].filter(Boolean).join(' ');
                          (dcRef.current as any).send(JSON.stringify({
                            type: 'conversation.item.create',
                            item: {
                              type: 'message',
                              role: 'user',
                              content: [{ type: 'input_text', text: userMsg }]
                            }
                          }));
                        } catch (e) {
                          console.warn('Unable to notify Tally of lesson MCQ selection:', e);
                        }
                      }
                    }
                  }];
                  setWorkspaceCards(cards);
                  setShowWorkspace(true);
                } catch (e) {
                  console.warn('Failed to preload lesson into workspace:', e);
                  setShowWorkspace(true);
                }

                // Start Tally with a lesson-focused scenario prompt. We include plan/lesson context.
                const lesson = (lessonInstance as any)?.lesson || {};
                const planTitle = (selectedLearningPlan?.title || selectedLearningPlan?.plan?.title || 'Learning Plan');
                const lessonTitle = String(lesson?.title || `Lesson ${lessonIndex + 1}`);
                const talkInstr = lesson?.talk_to_tally?.instructions ? String(lesson.talk_to_tally.instructions) : '';
                const talkGoal = lesson?.talk_to_tally?.goal ? String(lesson.talk_to_tally.goal) : '';
                const sessionPrompt = [
                  `You are Tally. This session is focused on a specific lesson from a learning plan.`,
                  `Learning plan: ${planTitle} (plan_id=${planId}).`,
                  `Lesson ${lessonIndex + 1}: ${lessonTitle}.`,
                  talkGoal ? `Session goal: ${talkGoal}` : '',
                  talkInstr ? `Instructions: ${talkInstr}` : '',
                  `Use the shared workspace lesson cards as the reference for what we’re practicing. Stay on this lesson unless the user asks to switch.`
                ].filter(Boolean).join('\n');

                await startConversation(sessionPrompt);
              }}
            />
          </div>
        )}
        {currentPage === 'feedback' && (
          <div className="p-6" style={{ backgroundColor: '#F5F1EF' }}>
            {renderFeedbackPage()}
          </div>
        )}
        {currentPage === 'practice' && (
          <div className="p-6" style={{ backgroundColor: '#F5F1EF' }}>
            {renderPracticePage()}
          </div>
        )}
        {currentPage === 'settings' && (
          <div className="h-full overflow-auto p-6" style={{ backgroundColor: '#F5F1EF' }}>
            <div className="bg-white rounded-lg shadow max-w-4xl mx-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
                <h2 className="text-xl font-semibold text-gray-800">History</h2>
                <button
                  onClick={() => setCurrentPage('chat')}
                  style={{
                    backgroundColor: '#F5F1EF',
                    borderRadius: '100px',
                    padding: '8px 16px',
                    color: '#25394D',
                    fontSize: '14px',
                    fontFamily: 'Lato',
                    fontWeight: '700',
                    wordWrap: 'break-word',
                    border: '1px solid #25394D',
                    cursor: 'pointer'
                  }}
                  className="hover:opacity-80 transition-opacity"
                >
                  Close
                </button>
              </div>
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <SettingsPanelEnhanced
                userEmail={user?.email}
                showUserTranscript={showUserTranscript}
                showAITranscript={showAITranscript}
                aiVoice={aiVoice}
                personalityType={personalityType}
                feedbackPrompt={feedbackPrompt}
                mcqPrompt={mcqPrompt}
                onToggleUserTranscript={() => setShowUserTranscript(prev => !prev)}
                onToggleAITranscript={() => setShowAITranscript(prev => !prev)}
                onChangeVoice={setAIVoice}
                onChangePersona={setPersonalityType}
                onChangeFeedbackPrompt={setFeedbackPrompt}
                onChangeMCQPrompt={setMCQPrompt}
                onResumeConversation={handleResumeConversation}
                historyOnly
              />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Debug drawer at bottom (collapsible, non-overlapping) */}
      {debugOpen && (
        <div
          className="bg-white border-t shadow-inner flex-shrink-0"
          style={{ maxHeight: debugCollapsed ? '36px' : '45vh', overflow: debugCollapsed ? 'hidden' : 'auto' }}
          ref={debugDrawerRef}
        >
          {/* Drawer header with chevron toggle */}
          <div className="px-4 py-2 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Session Instructions</h3>
            <button
              className="text-xs px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setDebugCollapsed(c => !c)}
              title={debugCollapsed ? 'Expand' : 'Collapse'}
            >
              {debugCollapsed ? 'Expand' : 'Collapse'}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {debugCollapsed ? (
                  // Chevron up (to indicate expand upwards)
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                ) : (
                  // Chevron down (to indicate collapse downwards)
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                )}
              </svg>
            </button>
          </div>

          {!debugCollapsed && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-800">Session Instructions (effective)</h3>
                <button
                  className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  onClick={() => {
                    try {
                      const text = String(localDebugInfo?.session?.instructions || '');
                      navigator.clipboard.writeText(text);
                    } catch {}
                  }}
                >
                  Copy
                </button>
              </div>
              <pre className="whitespace-pre-wrap text-xs border rounded p-3 bg-gray-50 max-h-[30vh] overflow-auto">
                {String(localDebugInfo?.session?.instructions || '(not available yet)')}
              </pre>
            </div>
            <div className="md:col-span-1">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Session Config</h3>
              <div className="text-xs text-gray-700 mb-3 border rounded p-2 bg-gray-50">
                <div><span className="font-medium">Model:</span> {String(localDebugInfo?.session?.model || '—')}</div>
                <div><span className="font-medium">Voice:</span> {String(localDebugInfo?.session?.voice || '—')}</div>
                <div><span className="font-medium">Modalities:</span> {Array.isArray(localDebugInfo?.session?.modalities) ? localDebugInfo?.session?.modalities.join(', ') : '—'}</div>
                <div><span className="font-medium">Tool choice:</span> {String(localDebugInfo?.session?.tool_choice || '—')}</div>
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Prompt Metadata</h3>
              <div className="text-xs text-gray-700">
                <div><span className="font-medium">Prompt ID:</span> {localDebugInfo?.meta?.promptId || '—'}</div>
                <div className="mt-2"><span className="font-medium">Resolved Variables:</span></div>
                <pre className="whitespace-pre-wrap text-xs border rounded p-2 bg-gray-50 max-h-[15vh] overflow-auto">
                  {JSON.stringify(localDebugInfo?.meta?.variables || localDebugInfo?.meta?.Variables || {}, null, 2)}
                </pre>
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mt-4 mb-2">Tool Calls</h3>
              <div className="space-y-2">
                {((localDebugInfo?.events || []).slice(-50)).map((e: any, idx: number) => {
                  const isReq = e.type === 'function_call' && e.phase === 'request';
                  const isRes = e.type === 'function_output' && e.phase === 'response';
                  const headerBg = isReq ? 'bg-blue-50 border-blue-200' : isRes ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200';
                  const headerText = isReq ? 'Request ↗' : isRes ? 'Response ↘' : e.type;
                  const dur = typeof e.durationMs === 'number' ? (e.durationMs >= 1000 ? `${(e.durationMs/1000).toFixed(1)} s` : `${e.durationMs} ms`) : undefined;
                  return (
                    <div key={idx} id={`dbg-${idx}`} className="border rounded">
                      <div className={`px-2 py-1 text-xs font-semibold border-b ${headerBg} flex items-center justify-between`}>
                        <div className="flex items-center gap-2">
                          <span>{headerText}</span>
                          {e.name ? <span className="text-gray-700">• {e.name}</span> : null}
                          {e.call_id ? <span className="text-gray-500">• id: {e.call_id}</span> : null}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span>{new Date(e.ts || Date.now()).toLocaleTimeString()}</span>
                          {isRes && dur ? <span className="text-gray-700">• {dur}</span> : null}
                        </div>
                      </div>
                      <div className="p-2">
                        {e.args ? (
                          <pre className="whitespace-pre-wrap text-[11px] border rounded p-2 bg-white">
                            {typeof e.args === 'string' ? e.args : JSON.stringify(e.args, null, 2)}
                          </pre>
                        ) : null}
                        {e.output ? (
                          <pre className="whitespace-pre-wrap text-[11px] border rounded p-2 bg-white mt-1">
                            {JSON.stringify(e.output, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TalkToTallyV2Project;

